/**
 * One episode's setup card — uploads + processing status + validation report.
 *
 * Extracted from the producer setup route so the whole project-setup feature
 * surface (UploadZone, validation, the upload hook) can be code-split via
 * `React.lazy`. The setup route stays tiny; the heavy lifting only loads when
 * a producer actually opens a project's setup page.
 */
import { Badge, Button, Card } from '@ohear/ui'
import { useProcessEpisode } from '../../lib/api/episodes'
import type { EpisodeSummary } from '../../lib/api/producer-types'
import { ProcessingStatus } from './ProcessingStatus'
import { UploadZone } from './UploadZone'
import { ValidationReport } from './ValidationReport'
import { useEpisodeUploads } from './useEpisodeUploads'

export function EpisodeSetupCard({ episode }: { episode: EpisodeSummary }) {
  const uploads = useEpisodeUploads(episode.id)
  const processEpisode = useProcessEpisode()

  const epUploads = episode.uploads ?? {
    video_object_key: null,
    ref_audio_object_key: null,
    script_object_key: null,
    srt_object_key: null,
  }
  const allRequired =
    uploads.requiredDone ||
    (Boolean(epUploads.video_object_key) && Boolean(epUploads.ref_audio_object_key))

  return (
    <Card
      title={
        <div className="flex items-center gap-3">
          <span className="text-[14px] font-medium">
            Episode {episode.episode_number} · {episode.title}
          </span>
          <Badge variant="gray">{episode.frame_rate_fps.toFixed(3)} fps</Badge>
          {episode.strategy ? <Badge variant="blue">Strategy {episode.strategy}</Badge> : null}
        </div>
      }
    >
      <ProcessingStatus state={episode.processing_state} percent={episode.processing_percent} />
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <UploadZone
          label="Reference video"
          required
          accept="video/*,.mov,.mp4,.mxf"
          maxBytes={20_000_000_000}
          hint="Drop a new cut here to replace."
          done={uploads.states.video.status === 'done' || Boolean(epUploads.video_object_key)}
          progress={
            uploads.states.video.status === 'uploading' ? uploads.states.video.progress : null
          }
          error={uploads.states.video.error}
          file={uploads.states.video.file}
          onFileSelected={(f) => void uploads.start('video', f)}
        />
        <UploadZone
          label="Reference audio"
          required
          accept="audio/*,.wav,.flac"
          maxBytes={5_000_000_000}
          hint="48 kHz / 24-bit recommended."
          done={
            uploads.states.ref_audio.status === 'done' || Boolean(epUploads.ref_audio_object_key)
          }
          progress={
            uploads.states.ref_audio.status === 'uploading'
              ? uploads.states.ref_audio.progress
              : null
          }
          error={uploads.states.ref_audio.error}
          file={uploads.states.ref_audio.file}
          onFileSelected={(f) => void uploads.start('ref_audio', f)}
        />
        <UploadZone
          label="Script (optional)"
          accept=".pdf,.docx,.txt,.fdx"
          maxBytes={50_000_000}
          hint="FDX / DOCX / PDF / TXT."
          done={uploads.states.script.status === 'done' || Boolean(epUploads.script_object_key)}
          progress={
            uploads.states.script.status === 'uploading' ? uploads.states.script.progress : null
          }
          error={uploads.states.script.error}
          file={uploads.states.script.file}
          onFileSelected={(f) => void uploads.start('script', f)}
        />
        <UploadZone
          label="SRT subtitles (optional)"
          accept=".srt"
          maxBytes={50_000_000}
          hint="Improves alignment fidelity."
          done={uploads.states.srt.status === 'done' || Boolean(epUploads.srt_object_key)}
          progress={uploads.states.srt.status === 'uploading' ? uploads.states.srt.progress : null}
          error={uploads.states.srt.error}
          file={uploads.states.srt.file}
          onFileSelected={(f) => void uploads.start('srt', f)}
        />
      </div>

      {episode.validation ? (
        <div className="mt-4">
          <ValidationReport report={episode.validation} />
        </div>
      ) : null}

      <div className="mt-4 flex justify-end">
        <Button
          variant="primary"
          onClick={() => void processEpisode.mutateAsync(episode.id)}
          loading={processEpisode.isPending}
          disabled={
            !allRequired ||
            episode.processing_state === 'queued' ||
            episode.processing_state === 'slicing'
          }
        >
          {episode.processing_state === 'ready' ? 'Reprocess' : 'Process now'}
        </Button>
      </div>
    </Card>
  )
}
